import { z } from 'zod';
import { minutesOfDay, zonedParts } from './time.js';
/**
 * Time zones and local time for people's profiles (docs/adr/0032-profiles-and-local-time.md).
 * Shared by the API (validation), the web app and the phone app (the "3:42 pm local time · 2 h behind
 * you" line). Built on Intl only, so there is no time-zone dependency and every platform agrees.
 */
const ZONE_SHAPE = /^[A-Za-z][A-Za-z0-9_+-]*(?:\/[A-Za-z0-9_+-]+){0,2}$/;
/** True for a name Intl accepts as an IANA time zone ("Asia/Kolkata", "UTC"). Never throws. */
export function isValidTimeZone(name) {
    if (typeof name !== 'string' || name.length < 1 || name.length > 64 || !ZONE_SHAPE.test(name))
        return false;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: name });
        return true;
    }
    catch {
        return false;
    }
}
export const TimeZoneName = z
    .string()
    .min(1)
    .max(64)
    .refine(isValidTimeZone, { message: 'Pick a time zone from the list' });
/** The device's own zone by its current name ("Asia/Kolkata", not "Asia/Calcutta"), or UTC when the platform does not say. */
export function deviceTimeZone() {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (!tz || !isValidTimeZone(tz))
            return 'UTC';
        const renamed = RENAMED[tz];
        return renamed && isValidTimeZone(renamed) ? renamed : tz;
    }
    catch {
        return 'UTC';
    }
}
const HHMM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use a time like 09:00');
const Weekday = z.number().int().min(0).max(6);
/**
 * When someone usually works, on their own wall clock. `end` before `start` means the hours run past
 * midnight (a night shift: 22:00 to 06:00); the day it starts on is the one that must be in `days`.
 */
export const WorkingHours = z
    .object({
    start: HHMM,
    end: HHMM,
    /** 0 = Sunday … 6 = Saturday. */
    days: z.array(Weekday).min(1).max(7),
})
    .refine((w) => w.start !== w.end, { message: 'Start and end must differ', path: ['end'] });
export const DEFAULT_WORKING_HOURS = { start: '09:00', end: '17:30', days: [1, 2, 3, 4, 5] };
// --- Offsets and differences ---------------------------------------------------------------------
/** Minutes the zone is ahead of UTC at `at` (Sydney in summer: 660; Kolkata: 330; New York in summer: -240). */
export function zoneOffsetMinutes(at, timeZone) {
    const minute = Math.floor(at.getTime() / 60_000) * 60_000;
    const p = zonedParts(new Date(minute), timeZone);
    return Math.round((Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) - minute) / 60_000);
}
/** "2 h", "30 min", "5 h 30 min", "45 min". */
export function formatDuration(minutes) {
    const m = Math.abs(Math.round(minutes));
    const h = Math.floor(m / 60);
    const r = m % 60;
    if (!h)
        return `${r} min`;
    return r ? `${h} h ${r} min` : `${h} h`;
}
/** "UTC+10", "UTC+5:30", "UTC−4" (a true minus sign, read as "minus" by screen readers). */
export function formatUtcOffset(minutes) {
    if (minutes === 0)
        return 'UTC';
    const sign = minutes > 0 ? '+' : '−';
    const m = Math.abs(minutes);
    const h = Math.floor(m / 60);
    const r = m % 60;
    return `UTC${sign}${h}${r ? `:${String(r).padStart(2, '0')}` : ''}`;
}
/**
 * How far their clock is from yours, in minutes (positive: they are ahead). DST-safe: both offsets are
 * taken at the same instant, so it changes on the day either zone changes its clocks.
 */
export function zoneDifferenceMinutes(at, theirZone, yourZone) {
    return zoneOffsetMinutes(at, theirZone) - zoneOffsetMinutes(at, yourZone);
}
/** "2 h behind you", "5 h 30 min ahead of you", "Same time as you". */
export function formatZoneDifference(diffMinutes) {
    if (diffMinutes === 0)
        return 'Same time as you';
    return `${formatDuration(diffMinutes)} ${diffMinutes > 0 ? 'ahead of you' : 'behind you'}`;
}
// --- Labels --------------------------------------------------------------------------------------
/** "3:42 pm": the wall-clock time in a zone, the same on every platform whatever its locale data. */
export function formatClock(at, timeZone) {
    const p = zonedParts(at, timeZone);
    return `${p.hour % 12 || 12}:${String(p.minute).padStart(2, '0')} ${p.hour < 12 ? 'am' : 'pm'}`;
}
// Locales whose short names are well known for their region ("AEDT", "EDT", "BST", "IST", "NZDT").
const ABBREVIATION_LOCALES = ['en-AU', 'en-US', 'en-GB', 'en-IN', 'en-NZ'];
const abbreviationFormatters = new Map();
/** A short zone name people recognise ("AEDT", "IST", "EDT"), else the UTC offset ("UTC+8"). */
export function zoneAbbreviation(at, timeZone) {
    if (timeZone === 'UTC' || timeZone === 'Etc/UTC')
        return 'UTC';
    let formatters = abbreviationFormatters.get(timeZone);
    if (!formatters) {
        formatters = [];
        for (const locale of ABBREVIATION_LOCALES) {
            try {
                formatters.push(new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: 'short' }));
            }
            catch {
                // Unknown zone or a platform without this locale: fall through to the offset.
            }
        }
        abbreviationFormatters.set(timeZone, formatters);
    }
    for (const f of formatters) {
        try {
            const name = f.formatToParts(at).find((p) => p.type === 'timeZoneName')?.value;
            if (name && /^[A-Z]{2,5}$/.test(name) && name !== 'GMT')
                return name;
        }
        catch {
            // formatToParts missing on an old engine.
        }
    }
    return formatUtcOffset(zoneOffsetMinutes(at, timeZone));
}
/** "Kolkata", "New York", "Sydney": the city part of a zone name. */
export function zoneCity(timeZone) {
    const last = timeZone.split('/').at(-1) ?? timeZone;
    return last.replace(/_/g, ' ');
}
// --- Working hours -------------------------------------------------------------------------------
/** Whether `at` falls inside the working hours, read on the person's own clock. */
export function isWithinWorkingHours(at, timeZone, hours) {
    const p = zonedParts(at, timeZone);
    const now = p.hour * 60 + p.minute;
    const start = minutesOfDay(hours.start);
    const end = minutesOfDay(hours.end);
    if (start < end)
        return hours.days.includes(p.weekday) && now >= start && now < end;
    // Past midnight: the evening part belongs to today, the early-morning part to yesterday's shift.
    if (now >= start)
        return hours.days.includes(p.weekday);
    if (now < end)
        return hours.days.includes((p.weekday + 6) % 7);
    return false;
}
/** Everything the profile shows about someone's time, for one instant. */
export function localTime(at, theirZone, yourZone, hours = null) {
    const diffMinutes = zoneDifferenceMinutes(at, theirZone, yourZone);
    const theirs = zonedParts(at, theirZone);
    const yours = zonedParts(at, yourZone);
    const day = (p) => Date.UTC(p.year, p.month - 1, p.day);
    const shift = Math.sign(day(theirs) - day(yours));
    return {
        clock: formatClock(at, theirZone),
        abbreviation: zoneAbbreviation(at, theirZone),
        diffMinutes,
        difference: formatZoneDifference(diffMinutes),
        dayShift: shift,
        working: hours ? isWithinWorkingHours(at, theirZone, hours) : null,
    };
}
/** "3:42 pm", "3:42 pm tomorrow", "11:10 pm yesterday". */
export function clockWithDay(t) {
    return t.dayShift === 1 ? `${t.clock} tomorrow` : t.dayShift === -1 ? `${t.clock} yesterday` : t.clock;
}
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** "9:00 am – 5:30 pm, Mon–Fri", "10:00 pm – 6:00 am, Sun, Tue, Thu". */
export function formatWorkingHours(hours) {
    const clock = (hhmm) => {
        const m = minutesOfDay(hhmm);
        const h = Math.floor(m / 60);
        return `${h % 12 || 12}:${String(m % 60).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`;
    };
    const days = [...new Set(hours.days)].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7));
    const order = days.map((d) => (d + 6) % 7); // Monday first
    const consecutive = order.length > 2 && order.every((d, i) => i === 0 || d === order[i - 1] + 1);
    const dayText = days.length === 7 ? 'every day' : consecutive ? `${DAY_LABELS[days[0]]}–${DAY_LABELS[days.at(-1)]}` : days.map((d) => DAY_LABELS[d]).join(', ');
    return `${clock(hours.start)} – ${clock(hours.end)}, ${dayText}`;
}
// --- The picker's list ---------------------------------------------------------------------------
// Used where the engine cannot list its zones (older Hermes): the zones agencies and their offshore teams use most.
const FALLBACK_ZONES = [
    'Pacific/Auckland', 'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane', 'Australia/Hobart', 'Australia/Adelaide',
    'Australia/Darwin', 'Australia/Perth', 'Asia/Tokyo', 'Asia/Seoul', 'Asia/Shanghai', 'Asia/Hong_Kong', 'Asia/Singapore',
    'Asia/Kuala_Lumpur', 'Asia/Manila', 'Asia/Jakarta', 'Asia/Bangkok', 'Asia/Ho_Chi_Minh', 'Asia/Dhaka', 'Asia/Kathmandu',
    'Asia/Kolkata', 'Asia/Colombo', 'Asia/Karachi', 'Asia/Dubai', 'Europe/Moscow', 'Europe/Istanbul', 'Africa/Nairobi',
    'Africa/Cairo', 'Africa/Johannesburg', 'Europe/Athens', 'Europe/Kyiv', 'Europe/Berlin', 'Europe/Paris', 'Europe/Madrid',
    'Europe/Amsterdam', 'Europe/Warsaw', 'Europe/London', 'Europe/Dublin', 'Europe/Lisbon', 'Atlantic/Reykjavik', 'UTC',
    'America/Sao_Paulo', 'America/Argentina/Buenos_Aires', 'America/Halifax', 'America/New_York', 'America/Toronto',
    'America/Chicago', 'America/Mexico_City', 'America/Denver', 'America/Phoenix', 'America/Los_Angeles', 'America/Vancouver',
    'America/Anchorage', 'Pacific/Honolulu', 'Pacific/Fiji',
];
// ICU (Node, Chrome) still lists some zones under their old names; people search for the current ones.
const RENAMED = {
    'Asia/Calcutta': 'Asia/Kolkata',
    'Asia/Saigon': 'Asia/Ho_Chi_Minh',
    'Asia/Katmandu': 'Asia/Kathmandu',
    'Asia/Rangoon': 'Asia/Yangon',
    'Asia/Dacca': 'Asia/Dhaka',
    'Europe/Kiev': 'Europe/Kyiv',
    'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
    'America/Godthab': 'America/Nuuk',
    'Atlantic/Faeroe': 'Atlantic/Faroe',
    'Pacific/Truk': 'Pacific/Chuuk',
    'Pacific/Ponape': 'Pacific/Pohnpei',
    'Pacific/Enderbury': 'Pacific/Kanton',
};
/** Every zone the platform knows (by its current name), plus `include` (the current value) if it is missing. */
export function timeZoneList(include = []) {
    let zones = [];
    try {
        const supported = Intl.supportedValuesOf;
        if (typeof supported === 'function')
            zones = supported('timeZone');
    }
    catch {
        zones = [];
    }
    if (!zones.length)
        zones = FALLBACK_ZONES.filter(isValidTimeZone);
    zones = [...new Set(zones.map((z) => (RENAMED[z] && isValidTimeZone(RENAMED[z]) ? RENAMED[z] : z)))];
    if (!zones.includes('UTC'))
        zones = [...zones, 'UTC'];
    for (const z of include)
        if (z && !zones.includes(z) && isValidTimeZone(z))
            zones = [...zones, z];
    return zones;
}
/** The picker's rows, west to east, then by name. */
export function timeZoneOptions(at, include = []) {
    return timeZoneList(include)
        .map((zone) => {
        const offsetMinutes = zoneOffsetMinutes(at, zone);
        return {
            zone,
            city: zoneCity(zone),
            region: zone.includes('/') ? (zone.split('/')[0] ?? '') : '',
            offsetMinutes,
            offset: formatUtcOffset(offsetMinutes),
            abbreviation: zoneAbbreviation(at, zone),
        };
    })
        .sort((a, b) => a.offsetMinutes - b.offsetMinutes || a.zone.localeCompare(b.zone));
}
/** Search by city, region, full name, abbreviation or offset ("kolkata", "asia", "IST", "+5:30", "5:30"). */
export function matchesTimeZone(option, query) {
    const q = query.trim().toLowerCase().replace(/_/g, ' ');
    if (!q)
        return true;
    const hay = [option.zone.replace(/_/g, ' '), option.city, option.abbreviation, option.offset, option.offset.replace('−', '-')]
        .join(' ')
        .toLowerCase();
    return q.split(/\s+/).every((w) => hay.includes(w));
}
//# sourceMappingURL=zones.js.map